/**
 * TL4KHeaderButton — pulsante "utility" dell'header (e del footer) della shell TL4K.
 *
 * Unica fonte di stile per Admin, Schermo intero, Partecipa, Materiali, I miei elaborati,
 * Lavagna e i controlli di TL4KAppHeader (docs/TL4K_APP_STANDARD.md §8).
 *
 * - Altezza `shellChrome.utilityButtonHeight` (32px), allineata al logo.
 * - Solo icona: quadrato 32×32. Con etichetta: larghezza minima `shellChrome.utilityButtonMinWidth`.
 * - Icona sempre a `shellChrome.utilityIconSize`.
 * - Colori solo da token `--tl4k-*`, con fallback presi da `colors`: alcune app che montano
 *   TL4KAppHeader non hanno ThemeProvider, e senza fallback il pulsante diventerebbe trasparente.
 *
 * Dipende solo da react e design-tokens: si può importare anche via
 * `@tl4k/frontend-shared/headerButton` (i test jest che mockano la radice del pacchetto
 * fanno `jest.requireActual` di questo sotto-percorso).
 */
import {
  isValidElement,
  useInsertionEffect,
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { borderRadius, colors, shellChrome, spacing, transitions, typography } from "../design-tokens";

export type TL4KHeaderButtonVariant = "default" | "neutral" | "primary" | "accent";

/** Componente icona (es. lucide) — riceve la size standard — o elemento già pronto (es. pallino colore). */
export type TL4KHeaderButtonIcon =
  | ComponentType<{ size?: number | string; "aria-hidden"?: boolean | "true" | "false" }>
  | ReactElement;

export interface TL4KHeaderButtonStyleOptions {
  variant?: TL4KHeaderButtonVariant;
  /** Stato attivo/selezionato: aspetto pieno primary su qualunque variante. */
  active?: boolean;
  disabled?: boolean;
  /** Senza etichetta: quadrato 32×32, niente padding. */
  iconOnly?: boolean;
}

export const TL4K_HEADER_BUTTON_ICON_SIZE = shellChrome.utilityIconSize;
export const TL4K_HEADER_BUTTON_CLASS = "tl4k-header-btn";

const v = (name: string, fallback: string) => `var(--tl4k-${name}, ${fallback})`;

type Palette = { border: string; background: string; color: string; filled: boolean };

const PALETTES: Record<TL4KHeaderButtonVariant, Palette> = {
  default: {
    border: v("primary-300", colors.primary[300]),
    background: v("surface-warm", colors.surface.warm),
    color: v("primary-700", colors.primary[700]),
    filled: false,
  },
  neutral: {
    border: v("neutral-300", colors.neutral[300]),
    background: v("neutral-50", colors.neutral[50]),
    color: v("neutral-700", colors.neutral[700]),
    filled: false,
  },
  primary: {
    border: v("primary-500", colors.primary[500]),
    background: v("primary-500", colors.primary[500]),
    color: v("surface-default", colors.surface.default),
    filled: true,
  },
  // secondary-900: unico arancio della scala con testo bianco ≥4.5:1 (WCAG AA).
  accent: {
    border: v("secondary-900", colors.secondary[900]),
    background: v("secondary-900", colors.secondary[900]),
    color: v("surface-default", colors.surface.default),
    filled: true,
  },
};

const DISABLED_FILLED: Palette = {
  border: v("neutral-400", colors.neutral[400]),
  background: v("neutral-400", colors.neutral[400]),
  color: v("surface-default", colors.surface.default),
  filled: true,
};

export function tl4kHeaderButtonStyle({
  variant = "default",
  active = false,
  disabled = false,
  iconOnly = false,
}: TL4KHeaderButtonStyleOptions = {}): CSSProperties {
  const base = active ? PALETTES.primary : PALETTES[variant];
  const palette = disabled && base.filled ? DISABLED_FILLED : base;
  return {
    boxSizing: "border-box",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: v("spacing-xs", spacing.xs),
    flexShrink: 0,
    minHeight: shellChrome.utilityButtonHeight,
    minWidth: iconOnly ? shellChrome.utilityButtonHeight : shellChrome.utilityButtonMinWidth,
    padding: iconOnly ? 0 : `0 ${v("spacing-sm", spacing.sm)}`,
    borderRadius: v("border-radius-md", borderRadius.md),
    // Longhand, non `border`/`background`: un override `style={{ borderColor }}` del chiamante
    // non entra in conflitto con la shorthand (warning React e stili persi al re-render).
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: palette.border,
    backgroundColor: palette.background,
    color: palette.color,
    fontFamily: v("font-family-body", typography.fontFamilyBody),
    fontSize: shellChrome.utilityButtonFontSize,
    fontWeight: 600,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    textDecoration: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? (base.filled ? 0.85 : 0.55) : 1,
  };
}

// Hover e focus non si esprimono con gli stili inline: un solo <style> condiviso, iniettato al primo uso.
const STYLE_ELEMENT_ID = "tl4k-header-btn-styles";
const STYLE_RULES = `
.${TL4K_HEADER_BUTTON_CLASS}{transition:filter ${v("transition-fast", transitions.fast)}}
.${TL4K_HEADER_BUTTON_CLASS}:not(:disabled):not([aria-disabled="true"]):hover{filter:brightness(0.94)}
.${TL4K_HEADER_BUTTON_CLASS}:focus-visible{outline:2px solid ${v("focus", colors.focus)};outline-offset:2px}
`;

function useHeaderButtonStyles() {
  useInsertionEffect(() => {
    if (typeof document === "undefined" || document.getElementById(STYLE_ELEMENT_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ELEMENT_ID;
    el.textContent = STYLE_RULES;
    document.head.appendChild(el);
  }, []);
}

function renderIcon(icon: TL4KHeaderButtonIcon | undefined): ReactNode {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  const Icon = icon;
  return <Icon size={TL4K_HEADER_BUTTON_ICON_SIZE} aria-hidden="true" />;
}

const hasLabel = (children: ReactNode) =>
  children !== undefined && children !== null && children !== false && children !== "";

interface OwnProps {
  variant?: TL4KHeaderButtonVariant;
  active?: boolean;
  icon?: TL4KHeaderButtonIcon;
  /** Etichetta visibile. Se assente il pulsante è solo icona: serve `aria-label`. */
  children?: ReactNode;
}

export type TL4KHeaderButtonProps = OwnProps & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children">;

export function TL4KHeaderButton({
  variant = "default",
  active = false,
  icon,
  children,
  className,
  style,
  type = "button",
  disabled,
  ...rest
}: TL4KHeaderButtonProps) {
  useHeaderButtonStyles();
  const iconOnly = !hasLabel(children);
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled}
      data-variant={variant}
      data-active={active ? "true" : "false"}
      className={className ? `${TL4K_HEADER_BUTTON_CLASS} ${className}` : TL4K_HEADER_BUTTON_CLASS}
      style={{ ...tl4kHeaderButtonStyle({ variant, active, disabled: !!disabled, iconOnly }), ...style }}
    >
      {renderIcon(icon)}
      {children}
    </button>
  );
}

export type TL4KHeaderLinkProps = OwnProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "children"> & { href: string };

/** Stesso aspetto di TL4KHeaderButton, ma è un link (es. console Admin). */
export function TL4KHeaderLink({
  variant = "default",
  active = false,
  icon,
  children,
  className,
  style,
  ...rest
}: TL4KHeaderLinkProps) {
  useHeaderButtonStyles();
  const iconOnly = !hasLabel(children);
  return (
    <a
      {...rest}
      data-variant={variant}
      data-active={active ? "true" : "false"}
      className={className ? `${TL4K_HEADER_BUTTON_CLASS} ${className}` : TL4K_HEADER_BUTTON_CLASS}
      style={{ ...tl4kHeaderButtonStyle({ variant, active, iconOnly }), ...style }}
    >
      {renderIcon(icon)}
      {children}
    </a>
  );
}
